/**
 * Cross-squad session check-in rules (directed).
 *
 * WASC: Performance swimmers may also attend Development sessions.
 * Development, Junior, and Fitness may only attend their own squad sessions.
 *
 * Future: per-club config e.g. squads.cross_check_in_squad_ids — MVP uses name edges.
 */

/** Directed guest access: swimmers from `from` may check in to `into` sessions. */
export const CROSS_SQUAD_GUEST_EDGES: readonly { from: string; into: string }[] = [
  { from: 'Performance', into: 'Development' },
] as const;

/** @deprecated Use CROSS_SQUAD_GUEST_EDGES — kept for any older imports. */
export const CROSS_SQUAD_CHECK_IN_PAIRS: readonly (readonly [string, string])[] =
  CROSS_SQUAD_GUEST_EDGES.map((e) => [e.from, e.into] as const);

function normalizeSquadName(name: string): string {
  return name.trim().toLowerCase();
}

/** Session squad names a swimmer from this home squad may guest into (excludes self). */
export function crossCheckInPartnerSquadNames(swimmerSquadName: string): string[] {
  const norm = normalizeSquadName(swimmerSquadName);
  const partners = new Set<string>();
  for (const edge of CROSS_SQUAD_GUEST_EDGES) {
    if (normalizeSquadName(edge.from) === norm) partners.add(edge.into);
  }
  return [...partners];
}

/**
 * Home squad names whose members may guest-check-in to this session squad
 * (excludes the session squad itself). Used for poolside/admin roster expansion.
 */
export function guestHomeSquadNamesForSession(sessionSquadName: string): string[] {
  const norm = normalizeSquadName(sessionSquadName);
  const homes = new Set<string>();
  for (const edge of CROSS_SQUAD_GUEST_EDGES) {
    if (normalizeSquadName(edge.into) === norm) homes.add(edge.from);
  }
  return [...homes];
}

/** True when a swimmer's primary squad may check in to this session's squad. */
export function canCheckInToSessionByName(
  swimmerSquadName: string | null | undefined,
  sessionSquadName: string | null | undefined,
): boolean {
  if (!swimmerSquadName || !sessionSquadName) return false;
  if (normalizeSquadName(swimmerSquadName) === normalizeSquadName(sessionSquadName)) return true;
  return crossCheckInPartnerSquadNames(swimmerSquadName).some(
    (p) => normalizeSquadName(p) === normalizeSquadName(sessionSquadName),
  );
}

/** True when swimmer is checking into a partner squad's session (not their own). */
export function isCrossSquadGuest(
  swimmerSquadName: string | null | undefined,
  sessionSquadName: string | null | undefined,
): boolean {
  if (!swimmerSquadName || !sessionSquadName) return false;
  if (normalizeSquadName(swimmerSquadName) === normalizeSquadName(sessionSquadName)) return false;
  return canCheckInToSessionByName(swimmerSquadName, sessionSquadName);
}
